/**
 * Where a monthly fee stands in its dunning, as a screen says it (`docs/plans/billing-ui.md`
 * § Wave 2, items 3.2 and 4.2). Pure, so a domain's card, the channel's dashboard and a spec read
 * one answer.
 *
 * **Every date is the server's**: `next_charge_on`, `grace_ends_on` and `suspended_at` are what
 * the worker's `recurring-charge-billing` pass wrote. The one figure this side holds is the
 * resume window, which that pass states as a constant and the row does not carry.
 */

import type { RecurringChargeEntry } from '@/api/usage/usage'

/**
 * How long after a suspension the resource still comes back on its own once the wallet can pay —
 * the worker's `RESUME_WINDOW_DAYS` (`crates/bin/worker/src/billing/recurring.rs`). Past it, only
 * its owner puts it back online.
 */
export const RESUME_WINDOW_DAYS = 30

const DAY_MS = 86_400_000

/**
 * - `active` — paid; `date` is the next charge.
 * - `unpaid` — `past_due`; `date` is the last day of grace, `daysLeft` how many remain until it.
 * - `offline` — `suspended`; `date` is the last day it comes back on its own at a top-up.
 * - `none` — cancelled, or no fee at all: nothing to say.
 */
export type FeeState =
  | { state: 'active'; date: string }
  | { state: 'unpaid'; date: string; daysLeft: number }
  | { state: 'offline'; date: string; daysLeft: number }
  | { state: 'none' }

export type FeeInput = Pick<
  RecurringChargeEntry,
  'status' | 'next_charge_on' | 'grace_ends_on' | 'suspended_at'
>

/** `YYYY-MM-DD` of a moment, in the reader's own calendar. */
export function localDay(moment: Date): string {
  const month = String(moment.getMonth() + 1).padStart(2, '0')
  const day = String(moment.getDate()).padStart(2, '0')
  return `${moment.getFullYear()}-${month}-${day}`
}

/** Whole days from `from` to `to`, both `YYYY-MM-DD`; never negative. */
export function daysBetween(from: string, to: string): number {
  const span = Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)
  return Number.isFinite(span) ? Math.max(0, Math.round(span / DAY_MS)) : 0
}

/** `day` plus `count` days, both `YYYY-MM-DD`. */
export function addDays(day: string, count: number): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + count * DAY_MS).toISOString().slice(0, 10)
}

export function feeStateOf(charge: FeeInput | null | undefined, now: Date = new Date()): FeeState {
  if (!charge) return { state: 'none' }
  const today = localDay(now)
  switch (charge.status) {
    case 'active':
      return { state: 'active', date: charge.next_charge_on }
    case 'past_due': {
      const date = charge.grace_ends_on ?? charge.next_charge_on
      return { state: 'unpaid', date, daysLeft: daysBetween(today, date) }
    }
    case 'suspended': {
      if (!charge.suspended_at) return { state: 'none' }
      const date = addDays(localDay(new Date(charge.suspended_at)), RESUME_WINDOW_DAYS)
      return { state: 'offline', date, daysLeft: daysBetween(today, date) }
    }
    default:
      return { state: 'none' }
  }
}

/** Whether the fee wants a top-up now: unpaid, or offline and still inside the resume window. */
export function feeNeedsTopUp(fee: FeeState): boolean {
  return fee.state === 'unpaid' || fee.state === 'offline'
}
